import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Form, Button, Modal, Alert } from 'react-bootstrap';
import api from '../../services/axios';

const TeleconnectCredits = (props) => {
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  const [textToFind, setTextToFind] = useState('');
  const [totalCredits, setTotalCredits] = useState(0);
  
  // Modal para añadir crédito
  const [showModal, setShowModal] = useState(false);
  const [newCredit, setNewCredit] = useState({ account: '', amount: '', days: 30, comment: '' });
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);

  function getDefaultStartDate() {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().slice(0, 10);
  }

  function getDefaultEndDate() {
    return new Date().toISOString().slice(0, 10);
  }

  const fetchCredits = async () => {
    setLoading(true);
    try {
      const response = await api.get('/teleconnect_credits', {
        params: { startDate, endDate, textToFind }
      });
      setCredits(response.data.paginator?.data || response.data.credits || response.data || []);
      setTotalCredits(response.data.totals?.totalIngresos || response.data.totalIngresos || 0);
      setError(null);
    } catch (err) {
      setError('Error al cargar créditos');
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCredits();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCredits();
  };

  const handleAddCredit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(null);
    try {
      const response = await api.post('/teleconnect_add_credit_entry', newCredit);
      if (response.data.error) {
        setSubmitError(response.data.error);
      } else {
        setSubmitSuccess('Crédito añadido correctamente');
        setNewCredit({ account: '', amount: '', days: 30, comment: '' });
        setTimeout(() => {
          setShowModal(false);
          setSubmitSuccess(null);
          fetchCredits();
        }, 1500);
      }
    } catch (err) {
      setSubmitError('Error al añadir crédito');
    }
  };

  const handleDeleteCredit = async (creditId) => {
    if (!window.confirm('¿Eliminar este crédito?')) return;
    try {
      await api.post('/teleconnect_delete_credit_entry', { creditIdToDelete: creditId });
      fetchCredits();
    } catch (err) {
      alert('Error al eliminar crédito');
    }
  };

  return (
    <Container className="mt-4">
      <h2>Créditos Teleconnect</h2>
      
      {/* Filtros */}
      <Form onSubmit={handleSearch} className="mb-4">
        <Row>
          <Col md={2}>
            <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Col>
          <Col md={2}>
            <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Col>
          <Col md={3}>
            <Form.Control type="text" placeholder="Buscar..." value={textToFind} onChange={(e) => setTextToFind(e.target.value)} />
          </Col>
          <Col md={2}>
            <Button type="submit" variant="primary">Buscar</Button>
          </Col>
          <Col md={3} className="text-end">
            <Button variant="success" onClick={() => setShowModal(true)}>+ Añadir Crédito</Button>
          </Col>
        </Row>
      </Form>

      {/* Total */}
      <div className="mb-3">
        <strong>Total: {totalCredits.toFixed(2)} €</strong>
      </div>

      {/* Tabla */}
      {loading ? (
        <p>Cargando...</p>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead className="bg-primary text-white">
            <tr>
              <th>Fecha</th>
              <th>Cuenta</th>
              <th>Nombre</th>
              <th>Comercial</th>
              <th>Importe</th>
              <th>Días</th>
              <th>Motivo</th>
              <th>Usuario</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {credits.map((credit) => (
              <tr key={credit.id}>
                <td>{credit.created_at ? credit.created_at.substring(0, 16).replace('T', ' ') : ''}</td>
                <td>{credit.account?.cuenta || ''}</td>
                <td>{credit.account?.nombreContacto || ''} {credit.account?.apellidosContacto || ''}</td>
                <td>{credit.account?.reseller?.nombre || ''}</td>
                <td style={{ color: credit.amount >= 0 ? 'green' : 'red', fontWeight: 'bold' }}>
                  {parseFloat(credit.amount).toFixed(2)} €
                </td>
                <td>{credit.days}</td>
                <td>{credit.reason}</td>
                <td>{credit.user?.name || ''} {credit.user?.surname || ''}</td>
                <td>
                  <Button variant="danger" size="sm" onClick={() => handleDeleteCredit(credit.id)}>X</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Modal Añadir Crédito */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Añadir Crédito</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddCredit}>
          <Modal.Body>
            {submitError && <Alert variant="danger">{submitError}</Alert>}
            {submitSuccess && <Alert variant="success">{submitSuccess}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Cuenta</Form.Label>
              <Form.Control 
                type="text" 
                placeholder="Número de cuenta" 
                value={newCredit.account}
                onChange={(e) => setNewCredit({...newCredit, account: e.target.value})}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Importe (€)</Form.Label>
              <Form.Control 
                type="number" 
                step="0.01"
                placeholder="0.00" 
                value={newCredit.amount}
                onChange={(e) => setNewCredit({...newCredit, amount: e.target.value})}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Días</Form.Label>
              <Form.Control 
                type="number" 
                value={newCredit.days}
                onChange={(e) => setNewCredit({...newCredit, days: e.target.value})}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Comentario</Form.Label>
              <Form.Control 
                type="text" 
                placeholder="Motivo del crédito" 
                value={newCredit.comment}
                onChange={(e) => setNewCredit({...newCredit, comment: e.target.value})}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button variant="primary" type="submit">Guardar</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default TeleconnectCredits;
